import { Helmet } from "react-helmet-async";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Play, Pause, ShoppingCart, Trash2, Eye, EyeOff } from "lucide-react";
import { useState, useEffect } from "react";
import { AudioPlayer } from "@/components/AudioPlayer";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAudioPlayer } from "@/components/AudioPlayerProvider";
import { SkipBack, SkipForward } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface BeatDetailsModalProps {
  beat: {
    id: string;
    title: string;
    producer: string;
    genre: string;
    bpm: number;
    image_url: string;
    image_full_url?: string;
    mp3_url: string;
    wav_url: string;
    flp_url: string;
    mp3_lease_price: number;
    wav_lease_price: number;
    flp_price: number;
    semi_exclusive_price: number;
    full_exclusive_price: number;
    is_hidden?: boolean;
  } | null;
  isOpen: boolean;
  onClose: () => void;
  onBeatDeleted?: () => void;
}

export const BeatDetailsModal = ({ beat, isOpen, onClose, onBeatDeleted }: BeatDetailsModalProps) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [enabledLicenses, setEnabledLicenses] = useState<string[]>([]);
  const [animatingLicenses, setAnimatingLicenses] = useState(false);
  const { toast } = useToast();
  const { 
    currentBeat, 
    isPlaying, 
    currentTime, 
    duration, 
    volume, 
    isMuted,
    playBeat, 
    togglePlayPause, 
    playNext, 
    playPrevious,
    handleSeek,
    handleVolumeChange,
    toggleMute,
    setModalOpen
  } = useAudioPlayer();

  useEffect(() => {
    const checkAdminStatus = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const { data, error } = await supabase.rpc('has_role', {
          _user_id: session.user.id,
          _role: 'admin'
        });
        
        if (error) {
          if (import.meta.env.DEV) console.error('Error checking admin status:', error);
        } else {
          setIsAdmin(data || false);
        }
      }
    };

    const fetchLicenseAvailability = async () => {
      if (!beat) return;
      
      // Check license availability using the database function
      const licenseTypes = ['mp3_lease', 'wav_lease', 'flp', 'semi_exclusive', 'full_exclusive'];
      const availabilityPromises = licenseTypes.map(async (licenseType) => {
        const { data, error } = await supabase.rpc('is_license_available', {
          beat_id_param: beat.id,
          license_type_param: licenseType
        });

        if (error) {
          if (import.meta.env.DEV) console.error('Error checking license availability:', error);
          return null;
        }
        
        return { licenseType, isAvailable: data };
      });
      
      const results = await Promise.all(availabilityPromises);
      const availableLicenses = results
        .filter(result => result && result.isAvailable)
        .map(result => result!.licenseType);
      
      // Also check if beat has price for each license (allow 0 for free beats)
      const licensesWithPrices = [];
      if (beat.mp3_lease_price !== undefined && beat.mp3_lease_price >= 0 && availableLicenses.includes("mp3_lease")) licensesWithPrices.push("mp3_lease");
      if (beat.wav_lease_price !== undefined && beat.wav_lease_price >= 0 && availableLicenses.includes("wav_lease")) licensesWithPrices.push("wav_lease");
      if (beat.flp_price !== undefined && beat.flp_price >= 0 && availableLicenses.includes("flp")) licensesWithPrices.push("flp");
      if (beat.semi_exclusive_price !== undefined && beat.semi_exclusive_price >= 0 && availableLicenses.includes("semi_exclusive")) licensesWithPrices.push("semi_exclusive");
      if (beat.full_exclusive_price !== undefined && beat.full_exclusive_price >= 0 && availableLicenses.includes("full_exclusive")) licensesWithPrices.push("full_exclusive");
      
      setEnabledLicenses(licensesWithPrices);
    };

    if (isOpen) {
      checkAdminStatus();
      fetchLicenseAvailability();
      setModalOpen(true);
      
      // Check if opened from buy button
      if (window.location.hash === '#buy') {
        setAnimatingLicenses(true);
        window.history.replaceState(null, '', window.location.pathname);
        setTimeout(() => setAnimatingLicenses(false), 3000);
      }
    } else {
      setModalOpen(false);
    }
  }, [isOpen, beat, setModalOpen]);

  if (!beat) return null;

  const togglePlay = () => {
    if (beat) {
      if (currentBeat?.id === beat.id) {
        togglePlayPause();
      } else {
        playBeat(beat);
      }
    }
  };

  const isCurrentBeatPlaying = currentBeat?.id === beat?.id && isPlaying;

  const handlePurchase = async (licenseType: string, price: number) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        toast({
          title: "Authentication Required",
          description: "Please sign in to purchase beats.",
          variant: "destructive",
        });
        return;
      }

      // Handle free beats - create purchase directly without payment
      if (price === 0) {
        const { error } = await supabase
          .from('purchases')
          .insert({
            user_id: session.user.id,
            beat_id: beat.id,
            license_type: licenseType.toLowerCase().replace(/\s+/g, "_"),
            price: 0
          });

        if (error) {
          throw error;
        }

        toast({
          title: "Free Beat Downloaded!",
          description: "The beat has been added to your collection.",
        });
        return;
      }

      // For paid beats, proceed with Stripe checkout
      const { data, error } = await supabase.functions.invoke("create-payment", {
        body: {
          beatId: beat.id,
          licenseType: licenseType.toLowerCase().replace(/\s+/g, "_"),
          currency: "usd"
        }
      });

      if (error) {
        throw error;
      }

      if (data?.url) {
        // Open Stripe checkout in a new tab
        window.open(data.url, '_blank');
      }
    } catch (error) {
      if (import.meta.env.DEV) console.error("Purchase failed:", error);
      toast({
        title: "Purchase Error",
        description: error.message || "Failed to complete purchase.",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async () => {
    if (!beat) return;
    
    setIsDeleting(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('delete-beat-with-storage', {
        body: { beatId: beat.id }
      });

      if (error) {
        throw error;
      }

      toast({
        title: "Success",
        description: data.storageFilesDeleted 
          ? "Beat and all files deleted successfully" 
          : "Beat deleted successfully (files preserved for existing purchases)",
      });
      onBeatDeleted?.();
      onClose();
    } catch (error) {
      if (import.meta.env.DEV) console.error("Delete error:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete beat",
        variant: "destructive",
      });
    }
    
    setIsDeleting(false);
    setShowDeleteDialog(false);
  };

  const handleToggleVisibility = async () => {
    if (!beat) return;
    
    try {
      const { error } = await supabase
        .from('beats')
        .update({ is_hidden: !beat.is_hidden })
        .eq('id', beat.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: `Beat ${beat.is_hidden ? 'shown' : 'hidden'} successfully`,
      });
      
      onBeatDeleted?.(); // Refresh the beats list
    } catch (error) {
      if (import.meta.env.DEV) console.error("Error updating visibility:", error);
      toast({
        title: "Error",
        description: "Failed to update beat visibility",
        variant: "destructive",
      });
    }
  };

  const beatUrl = `https://skpills.com/beats?beat=${beat.id}`;
  const lowestPrice = Math.min(
    beat.mp3_lease_price || Infinity,
    beat.wav_lease_price || Infinity,
    beat.flp_price || Infinity,
    beat.semi_exclusive_price || Infinity,
    beat.full_exclusive_price || Infinity
  );
  const beatDesc = `${beat.title} by ${beat.producer} — ${beat.genre} beat at ${beat.bpm} BPM. Lease or buy exclusive rights on Skpills.`;
  const productLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: beat.title,
    description: beatDesc,
    image: beat.image_full_url || beat.image_url,
    brand: { "@type": "Brand", name: "Skpills" },
    category: beat.genre,
    offers: {
      "@type": "Offer",
      url: beatUrl,
      priceCurrency: "USD",
      price: isFinite(lowestPrice) ? lowestPrice : 0,
      availability: "https://schema.org/InStock",
      seller: { "@type": "Organization", name: "Skpills" }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      {isOpen && (
        <Helmet>
          <title>{`${beat.title} by ${beat.producer} — Skpills Beats`}</title>
          <meta name="description" content={beatDesc} />
          <link rel="canonical" href={beatUrl} />
          <meta property="og:title" content={`${beat.title} by ${beat.producer} — Skpills`} />
          <meta property="og:description" content={beatDesc} />
          <meta property="og:url" content={beatUrl} />
          <meta property="og:type" content="music.song" />
          <meta property="og:image" content={beat.image_full_url || beat.image_url} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={`${beat.title} by ${beat.producer} — Skpills`} />
          <meta name="twitter:description" content={beatDesc} />
          <meta name="twitter:image" content={beat.image_full_url || beat.image_url} />
          <script type="application/ld+json">{JSON.stringify(productLd)}</script>
        </Helmet>
      )}
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-2xl font-bold">{beat.title}</DialogTitle>
              <DialogDescription>by {beat.producer}</DialogDescription>
            </div>
            {isAdmin && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleToggleVisibility}
                >
                  {beat.is_hidden ? <Eye className="h-4 w-4 mr-2" /> : <EyeOff className="h-4 w-4 mr-2" />}
                  {beat.is_hidden ? 'Show' : 'Hide'}
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setShowDeleteDialog(true)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              </div>
            )}
          </div>
        </DialogHeader>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left Column - Image and Audio Player */}
          <div className="space-y-4">
            <div className="relative">
              <img
                src={beat.image_full_url || beat.image_url}
                alt={`${beat.title} artwork`}
                className="w-full aspect-square object-cover rounded-lg"
              />
            </div>

            
            <AudioPlayer
              beatTitle={beat.title}
              isPlaying={isCurrentBeatPlaying}
              onPlayPause={togglePlay}
              currentTime={currentTime}
              duration={duration}
              volume={volume}
              isMuted={isMuted}
              onSeek={handleSeek}
              onVolumeChange={handleVolumeChange}
              onMuteToggle={toggleMute}
            />
          </div>

          {/* Right Column - Details and Pricing */}
          <div className="space-y-6">
            {/* Beat Info */}
            <div className="space-y-3">
              <h3 className="text-lg font-semibold">Beat Information</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-muted-foreground">Producer:</span>
                  <span className="ml-2 font-medium">{beat.producer}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Genre:</span>
                  <span className="ml-2 font-medium">{beat.genre}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">BPM:</span>
                  <span className="ml-2 font-medium">{beat.bpm}</span>
                </div>
              </div>
            </div>

            {/* Pricing Options */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">License Options</h3>
              
              <div className="space-y-3">
                {(() => {
                  const isFree = (beat.mp3_lease_price === 0) || (beat.wav_lease_price === 0);
                  const options = [
                    { type: "MP3 Lease", price: beat.mp3_lease_price, key: "mp3_lease", displayName: "MP3 Lease" },
                    { type: "WAV Lease", price: beat.wav_lease_price, key: "wav_lease", displayName: "WAV Lease" },
                    { type: "flp", price: beat.flp_price, key: "flp", displayName: "FLP File" },
                    { type: "Semi Exclusive", price: beat.semi_exclusive_price, key: "semi_exclusive", displayName: "Semi Exclusive" },
                    { type: "Full Exclusive", price: beat.full_exclusive_price, key: "full_exclusive", displayName: "Full Exclusive" },
                  ];
                  return options
                    .map(({ type, price, key, displayName }) => {
                      // Hide exclusive options on free beats
                      if (isFree && (key === 'semi_exclusive' || key === 'full_exclusive')) return null;
                      // Hide options with null/undefined/negative price
                      if (price === undefined || price === null || price < 0) return null;
                      const isEnabled = enabledLicenses.includes(key);
                      return (
                        <Card key={key} className={`p-4 relative overflow-hidden ${!isEnabled ? 'opacity-60' : ''} ${
                          animatingLicenses && isEnabled ? 'license-border-animate' : ''
                        }`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className={`font-medium ${!isEnabled ? 'text-muted-foreground' : ''}`}>{displayName}</h4>
                              <p className="text-sm text-muted-foreground">
                                {key === "mp3_lease" && "Standard quality, perfect for demos"}
                                {key === "wav_lease" && "High quality uncompressed audio"}
                                {key === "flp" && "Complete project file for customization"}
                                {key === "semi_exclusive" && "Limited exclusive rights"}
                                {key === "full_exclusive" && "Complete ownership and rights"}
                                {!isEnabled && " - Currently unavailable"}
                              </p>
                            </div>
                            <div className="text-right">
                              <div className={`text-lg font-bold ${isEnabled ? 'text-primary' : 'text-muted-foreground'}`}>
                                {price === 0 ? 'FREE' : `$${price}`}
                              </div>
                              <Button
                                onClick={() => isEnabled && handlePurchase(type, price)}
                                size="sm"
                                className="mt-2"
                                disabled={!isEnabled}
                                variant={isEnabled ? "default" : "outline"}
                              >
                                <ShoppingCart className="h-4 w-4 mr-2" />
                                {isEnabled ? (price === 0 ? 'Download' : 'Buy') : 'Unavailable'}
                              </Button>
                            </div>
                          </div>
                        </Card>
                      );
                    })
                    .filter(Boolean);
                })()}
              </div>
            </div>
          </div>
        </div>

        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Beat</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "{beat.title}"? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={isDeleting}
                className="bg-destructive hover:bg-destructive/90"
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
};